import React from "react";
import { useTheme, styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Hidden, Switch, Grid, CircularProgress, Box } from "@mui/material";
import Clock from "./Clock";
import ConfirmModal from "../Common/CommonModal";
import { removeAuthObjects, toastError, toastSuccess, hasLabelRequestAccess } from "../../utils";
import appLogo from "../../assets/img/Fronic_logo_black.png";
import { Link, useNavigate } from "react-router-dom";
import * as commonAction from "../Common/actions";
import FullScreenLoader from "../Common/FullScreenLoader";

//icons
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CustomAddModal from "../Common/CustomAddModal";
import GavelIcon from "@mui/icons-material/Gavel";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import HearingIcon from "@mui/icons-material/Hearing";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import CampaignIcon from "@mui/icons-material/Campaign";
import MusicNote from "@mui/icons-material/MusicNote";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const drawerWidth = 270;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: '300ms',
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: '300ms',
    }),
  }),
}));

const DrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: '300ms',
    }),
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: '300ms',
      }),
    },
  }),
  ...(!open && {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: '300ms',
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(9)} + 1px)`,
    },
    '& .MuiDrawer-paper': {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: '300ms',
      }),
      overflowX: 'hidden',
      width: `calc(${theme.spacing(7)} + 1px)`,
      [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
      },
    },
  }),
}));

const initialListItems = [
  {
    label: "Dashboard",
    icon: <DashboardIcon />,
    path: "/",
  },
  {
    label: "Review Releases",
    icon: <NewReleasesIcon />,
    path: `/releases/review`,
  },
  {
    label: "Approved Releases",
    icon: <NewReleasesIcon />,
    path: "/release/approved",
  },
  {
    label: "Rejected Releases",
    icon: <NewReleasesIcon />,
    path: "/release/rejected",
  },
  {
    label: "Takendown Releases",
    icon: <RemoveCircleOutlineIcon />,
    path: "/release/takendown",
  },
  {
    label: "Video Releases",
    icon: <SlowMotionVideoIcon />,
    path: "/video_releases/review",
  },
  {
    label: "Video History",
    icon: <VoiceChatIcon />,
    path: "/video_releases/history",
  },
  {
    label: "UGC Claims",
    icon: <GavelIcon />,
    path: "/ugc_claims",
  },
  {
    label: "Profile Linking",
    icon: <SupervisorAccountIcon />,
    path: "/profile_linking",
  },
  {
    label: "Playlist Pitching",
    icon: <HearingIcon />,
    path: "/playlist_pitching",
  },
  {
    label: "New Label Requests",
    icon: <PersonAddIcon />,
    path: "/label-requests",
  },
  {
    label: "Admin Actions",
    icon: <ManageAccountsIcon />,
    path: "/user_actions",
  },
  {
    label: "User Details",
    icon: <RecentActorsIcon />,
    path: "/user_details",
  },
  // {
  //   label: "Artists",
  //   icon: <ContactsIcon />,
  //   path: "/artists",
  // },
  {
    label: "Report Requests",
    icon: <AssignmentIcon />,
    path: "/report_requests",
  },
  {
    label: "Tickets",
    icon: <ConfirmationNumberIcon />,
    path: "/tickets",
  },
  {
    label: "Whitelisting",
    icon: <PlaylistPlayIcon />,
    path: "/whitelisting",
  },
  {
    label: "Promotions",
    icon: <CampaignIcon />,
    path: "/promotions",
  },
  {
    label: "Requested Payouts",
    icon: <MonetizationOnIcon />,
    path: "/payout",
  },
  {
    label: "Lyrics Distribution",
    icon: <MusicNote />,
    path: "/lyrics_distribution",
  },
  {
    label: "Logout",
    icon: <MeetingRoomIcon />,
    path: "/login",
  },
];

export default function MainContainer(props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [confirmOpen, setConfimOpen] = React.useState(false);
  const [listItems, setListItems] = React.useState([...initialListItems]);
  const [awayStatus, setAwayStatus] = React.useState(false);
  const [awayText, setAwayText] = React.useState("");
  const [openAwayText, setOpenAwayText] = React.useState(false);
  const [sidenavReady, setSidenavReady] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSidenavReady(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    const canAccessLabelRequests = hasLabelRequestAccess();
    commonAction
      .countMeterAdmin()
      .then((res) => {
        let updatedList = [];
        const {
          in_review = 0,
          open_tickets = 0,
          reports_requested = 0,
          withdrawl_requested = 0,
          ugc_claim = 0,
          profile_linking = 0,
          pitch_playlist = 0,
          distribute_lyrics = 0,
          video_releases = 0,
          whitelisting = 0,
          label_requests = 0,
        } = res.body || {};
        for (let i = 0; i < initialListItems.length; i++) {
          const item = initialListItems[i];
          // Skip New Label Requests if user doesn't have access
          if (item.label === "New Label Requests" && !canAccessLabelRequests) {
            continue;
          }
          if (item.label === "Review Releases")
            updatedList.push({
              ...item,
              label: `${item.label} (${in_review})`,
            });
          else if (item.label === "Tickets")
            updatedList.push({
              ...item,
              label: `${item.label} (${open_tickets})`,
            });
          else if (item.label === "Report Requests")
            updatedList.push({
              ...item,
              label: `${item.label} (${reports_requested})`,
            });
          else if (item.label === "Requested Payouts")
            updatedList.push({
              ...item,
              label: `${item.label} (${withdrawl_requested})`,
            });
          else if (item.label === "UGC Claims")
            updatedList.push({
              ...item,
              label: `${item.label} (${ugc_claim})`,
            });
          else if (item.label === "Profile Linking")
            updatedList.push({
              ...item,
              label: `${item.label} (${profile_linking})`,
            });
          else if (item.label === "Playlist Pitching")
            updatedList.push({
              ...item,
              label: `${item.label} (${pitch_playlist})`,
            });
          else if (item.label === "Lyrics Distribution")
            updatedList.push({
              ...item,
              label: `${item.label} (${distribute_lyrics})`,
            });
          else if (item.label === "Video Releases")
            updatedList.push({
              ...item,
              label: `${item.label} (${video_releases})`,
            });
          else if (item.label === "Whitelisting")
            updatedList.push({
              ...item,
              label: `${item.label} (${whitelisting})`,
            });
          else if (item.label === "New Label Requests")
            updatedList.push({
              ...item,
              label: `${item.label} (${label_requests})`,
            });
          else updatedList.push({ ...item });
        }
        setListItems(updatedList);
      })
      .catch(() => {});
    commonAction.checkAwayStatus().then((res) => {
      setAwayStatus(
        res && res.body && res.body.status && res.body.status === "yes"
          ? true
          : false || false
      );
      setAwayText((res && res.body && res.body.text) || "");
    });
  }, []);

  // Admin heartbeat - sends heartbeat every 5 minutes to track online status
  React.useEffect(() => {
    // Initial heartbeat
    commonAction.adminHeartbeat().catch(console.error);

    // Send heartbeat every 5 minutes (300000ms)
    const heartbeatInterval = setInterval(() => {
      commonAction.adminHeartbeat().catch(console.error);
    }, 300000);

    return () => clearInterval(heartbeatInterval);
  }, []);

  React.useEffect(() => {
    window.addEventListener("resize", (event) => {
      // console.log(document.body.clientWidth + ' wide by ' + document.body.clientHeight + ' high');
      if (document.body.clientWidth < 700) {
        if (open) setOpen(false);
      } else {
        if (!open) setOpen(true);
      }
    });
    return () => {
      window.removeEventListener("resize", () => {});
    };
  }, [open]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const header = () => {
    const path = props.location?.pathname || props.match?.path || '';
    switch (path) {
      case "/":
        return "Dashboard";
      case "/news":
        return "News";
      case "/releases/review":
        return "Review Releases";
      case "/release/approved":
        return "Approved Releases";
      case "/release/rejected":
        return "Rejected Releases";
      case "/user_details":
        return "User Details";
      case "/user_actions":
        return "Admin Actions";
      case "/report_requests":
        return "Report Requests";
      case "/tickets":
        return "Tickets";
      case "/payout":
        return "Requested Payouts";
      case "/notifications":
        return "Notifications";
      case "/ugc_claims":
        return "UGC Claims";
      case "/profile_linking":
        return "Profile Linking";
      case "/playlist_pitching":
        return "Playlist Pitching";
      default: {
        return "";
      }
    }
  };

  const handleConfirmClickOpen = () => {
    setConfimOpen(true);
  };

  const handleConfirmClose = () => {
    setConfimOpen(false);
  };

  const onConfirmLogout = () => {
    onSetLoader(true);
    removeAuthObjects();
    setTimeout(() => {
      onSetLoader(false);
      return navigate("/login");
    }, 1000);
  };

  const onChangeAwayStatus = (checked = false) => {
    // const { checked } = e.target
    onSetLoader(true);
    commonAction
      .changeAwayStatusAdmin({
        status: checked ? "yes" : "no",
        text: awayText || "",
      })
      .then((res) => {
        onSetLoader(false);
        setAwayStatus(checked);
        setOpenAwayText(false);
        toastSuccess(res.message || "Status changed successfully");
      })
      .catch((err) => {
        onSetLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onSetLoader = (action) => {
    setLoader(action);
  };

  const onSetAwayText = (e) => {
    if (!awayStatus) {
      setOpenAwayText(true);
    } else {
      onChangeAwayStatus(false);
    }
  };
  return (
    <>
      <FullScreenLoader open={loader} />
      <div style={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: '36px',
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <div
              style={{
                width: "100%",
                justifyContent: "space-between",
                display: "flex",
              }}
            >
              <Typography variant="h6" noWrap style={{ alignSelf: "center" }}>
                {header()}
              </Typography>
              <Hidden only={["xs"]}>
                <div
                  style={{
                    alignSelf: "center",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <p>({awayStatus ? "Away" : "Available"})</p>
                  <Switch
                    checked={awayStatus}
                    style={{ color: "#65ffcd" }}
                    onChange={onSetAwayText}
                  />
                  <p
                    style={{
                      paddingRight: "23px",
                      marginRight: "23px",
                      fontSize: "18px",
                      borderRight: "1px solid #d8d8d8",
                    }}
                  >
                    WELCOME, SUB-ADMIN.
                  </p>
                  <Clock />
                </div>
              </Hidden>
              <Hidden only={["sm", "md", "lg", "xl"]}>
                <div
                  style={{
                    alignSelf: "center",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Switch
                    checked={awayStatus}
                    style={{ color: "#65ffcd" }}
                    onChange={onSetAwayText}
                  />
                </div>
              </Hidden>
            </div>
          </Toolbar>
        </AppBar>
        <DrawerStyled variant="permanent" open={open}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 8px',
            minHeight: '64px',
          }}>
            <div style={{
              width: '100%',
              height: '40px',
              display: 'flex',
            }}>
              <img
                style={{
                  margin: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  cursor: 'pointer'
                }}
                src={appLogo}
                alt=""
                onClick={() => navigate("/")}
              />
            </div>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </div>
          <Divider />
          <List>
            {!sidenavReady && (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={30} sx={{ color: '#12B4A1' }} />
              </Box>
            )}
            {sidenavReady && listItems.map((text, index) => {
              if (text.label === "Logout") {
                return (
                  <ListItem
                    style={{
                      backgroundColor:
                        (props.location?.pathname || '') === text.path ? "#dedede" : "",
                    }}
                    button
                    key={text.label}
                    onClick={handleConfirmClickOpen}
                  >
                    <ListItemIcon style={{ minWidth: 40 }}>
                      {text.icon}
                    </ListItemIcon>
                    <ListItemText primary={text.label} />
                  </ListItem>
                );
              } else {
                return (
                  <Link
                    className="link-color-black"
                    key={text.label}
                    to={text.path}
                  >
                    <ListItem
                      style={{
                        backgroundColor:
                          (props.location?.pathname || '') === text.path
                            ? "#dedede"
                            : "",
                      }}
                      button
                    >
                      <ListItemIcon style={{ minWidth: 40 }}>
                        {text.icon}
                      </ListItemIcon>
                      <ListItemText primary={text.label} />
                    </ListItem>
                  </Link>
                );
              }
            })}
          </List>
        </DrawerStyled>
        <main style={{ flexGrow: 1, padding: '24px' }}>
          <div style={{ minHeight: '64px' }} />
          {React.cloneElement(props.children, { setLoader: onSetLoader })}
        </main>
        <ConfirmModal
          message={`Are you sure you want to Logout?`}
          open={confirmOpen}
          title={`Confirm Logout`}
          onSubmitConfirm={onConfirmLogout}
          handleClose={handleConfirmClose}
        ></ConfirmModal>

        <CustomAddModal
          open={openAwayText}
          saveLabel="Set Away"
          title="Add Away Text"
          handleClose={() => setOpenAwayText(false)}
          onSubmitConfirm={() => onChangeAwayStatus(true)}
        >
          <Grid container style={{ height: "330px", display: "block" }}>
            <Grid item xs={12} style={{ height: "calc(100% - 60px)" }}>
              <textarea
                className="text-area-input"
                placeholder="Please add a text"
                value={awayText}
                onChange={(e) => setAwayText(e.target.value)}
              />
            </Grid>
          </Grid>
        </CustomAddModal>
      </div>
    </>
  );
}
